<template>
  <div
    :class="
      classes(
        n(),
        n(`theme--${theme}`),
      )
    "
  >
    <!-- 左侧区域：返回控件 -->
    <div
      v-if="showBackControl"
      :class="n('back-button')"
      :aria-label="backText"
      @click="handleBack"
    >
      <slot
        v-if="$slots['back-icon']"
        :class="n('back-button-icon')"
        name="back-icon"
      />
      <component
        :is="backIcon"
        v-else
        width="24"
        height="48"
        :class="n('back-button-icon')"
        @click="handleBack"
      />
    </div>

    <!-- 搜索输入区域 -->
    <div :class="n('input')">
      <!-- 左侧图标 -->
      <div
        :class="n('input-left')"
        @click="handleLeftIconClick"
      >
        <slot
          v-if="$slots['left-icon']"
          name="left-icon"
          :class="n('input-left-icon')"
        />
        <component
          :is="leftIcon"
          v-else
          :class="n('input-left-icon')"
          @click="handleLeftIconClick"
        />
      </div>

      <!-- 输入容器 -->
      <input
        ref="inputRef"
        :class="classes(n('input-inner'))"
        type="search"
        :cursor-color="cursorColor"
        :placeholder-class="n('input-inner-placeholder')"
        :placeholder="placeholder"
        :maxlength="maxlength"
        :value="inputValue"
        :aria-label="placeholder"
        @focus="handleFocus"
        @blur="handleBlur"
        @input="handleInput"
        @confirm="handleConfirm"
      >

      <!-- 清除按钮：用 v-show 避免 input 时实例化图标组件导致的掉帧 -->
      <div
        v-show="showClear"
        :class="n('input-clear')"
        role="button"
        :aria-label="clearText"
        @click="handleClear"
      >
        <CloseCircleIcon :class="n('input-clear-icon')" />
      </div>

      <!-- placeholder 浮层：独立覆盖在输入区域上方，点击即聚焦输入框 -->
      <div
        v-if="isPlaceholderVisible"
        :class="n('input-placeholder-overlay')"
        @click="handleOverlayClick"
      >
        <div :class="n('input-placeholder-overlay-left')">
          <slot
            v-if="$slots['left-icon']"
            name="left-icon"
            :class="n('input-placeholder-overlay-left-icon')"
          />
          <component
            :is="leftIcon"
            v-else
            :class="n('input-placeholder-overlay-left-icon')"
          />
        </div>
        <span :class="n('input-placeholder-overlay-text')">{{ placeholder }}</span>
      </div>
    </div>

    <!-- 右侧操作区 -->
    <div
      v-if="isFocused"
      :class="n('action')"
    >
      <slot
        v-if="$slots.action"
        name="action"
      />
      <div
        v-else
        :class="n('cancel')"
        role="button"
        :aria-label="cancelText"
        @click="handleCancel"
      >
        {{ cancelText }}
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from 'virtual-vue';
import { CloseCircleIcon } from '@tencent/wphk-design-icons';
import { createNamespace } from '../utils/components';
import { emits, useEmits } from './emits';
import { props } from './props';
import { useVModel } from '../hooks/use-v-model';

const { name, n, classes } = createNamespace('search');

export default defineComponent({
  name,
  components: {
    CloseCircleIcon,
  },
  props,
  emits,
  setup(props) {
    const emit = useEmits();

    const inputRef = ref<HTMLInputElement | null>(null);
    const isFocused = ref(false);
    const inputValue = useVModel<string>(props);

    const hasValue = computed(() => inputValue.value !== '');
    const showClear = computed(() => {
      if (!props.clearable) return false;
      if (props.clearTrigger === 'always') return hasValue.value;
      return hasValue.value && isFocused.value;
    });
    const isPlaceholderVisible = computed(() => !isFocused.value && !hasValue.value);

    const clearInputValue = () => {
      inputValue.value = '';
    };

    const toggleFocus = (isToggle: boolean) => {
      isFocused.value = isToggle;
    };

    const handleInput = (e: Event) => {
      const target = e.target as HTMLInputElement;
      inputValue.value = target.value;
    };

    const handleFocus = (e: FocusEvent) => {
      toggleFocus(true);
      emit('focus', e);
    };

    const handleBlur = (e: FocusEvent) => {
      emit('blur', e);
    };

    const handleClear = (e: Event) => {
      clearInputValue();
      emit('clear', e);
    };

    const handleCancel = () => {
      toggleFocus(false);
      clearInputValue();
      emit('cancel');
    };

    const handleBack = (e: Event) => {
      emit('back', e);
    };

    const handleLeftIconClick = (e: MouseEvent) => {
      emit('click-left-icon', e);
    };

    const handleConfirm = () => {
      emit('search', inputValue.value);
    };

    const handleOverlayClick = () => {
      inputRef.value?.focus();
    };

    return {
      n,
      classes,
      inputRef,
      inputValue,
      isFocused,
      isPlaceholderVisible,
      hasValue,
      showClear,
      handleInput,
      handleFocus,
      handleBlur,
      handleClear,
      handleCancel,
      handleBack,
      handleLeftIconClick,
      handleConfirm,
      handleOverlayClick,
    };
  },
});
</script>
